import * as React from "react";
import { Popover, PopoverContent, PopoverTrigger } from ".././popover";
import { DayPicker } from "react-day-picker";
import { format } from "date-fns/format";
import { Button } from ".././button";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";

export type DatePickerHandle = {
    setOpen: (value: boolean) => void;
    getOpen: () => boolean;
};

export interface DatePickerProps {
    value?: Date;
    onChange?: (date: Date | undefined) => void;
    disabled?: boolean;
    className?: string;
    placeholder?: string;
    placeholderIconColor?: string;
    minDate?: Date;
    dateFormat?: string;
    hideIcon?: boolean;
}

export const DatePicker = React.forwardRef<DatePickerHandle, DatePickerProps>(function DatePicker({
    value,
    onChange,
    disabled,
    className,
    placeholder = "Pick a date",
    placeholderIconColor = "#2563eb",
    minDate,
    dateFormat = "yyyy-MM-dd",
    hideIcon = false
}: DatePickerProps, ref: React.Ref<DatePickerHandle>) {
    const [open, setOpen] = React.useState(false);

    const handleSelect = (date: Date | undefined) => {
        onChange?.(date);
        if (date) {
            setOpen(false);
        }
    };

    React.useImperativeHandle(ref, () => ({
        setOpen: (v: boolean) => setOpen(v),
        getOpen: () => open,
    }), [open]);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    className={className + " w-full justify-start text-left font-normal flex items-center"}
                    disabled={disabled}
                >
                    {!hideIcon && <Calendar stroke={placeholderIconColor} />}
                    {value ? format(value, dateFormat) : <span>{placeholder}</span>}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="p-3 w-full z-[9999]" align="start">
                <DayPicker
                    mode="single"
                    selected={value}
                    onSelect={handleSelect}
                    disabled={disabled || (minDate ? (date) => date < minDate : undefined)}
                    components={{
                        Chevron: (props) =>
                            props.orientation === "left" ? (
                                <ChevronLeft stroke={placeholderIconColor} />
                            ) : (
                                <ChevronRight stroke={placeholderIconColor} />
                            ),
                    }}
                    modifiersStyles={{
                        selected: {
                            backgroundColor: `${placeholderIconColor}`,
                            color: "white",
                            borderRadius: "9999px",
                            border: "none"
                        },
                        today: {
                            fontWeight: "bold",
                            color: `${placeholderIconColor}`
                        },
                    }}
                />
            </PopoverContent>
        </Popover>
    );
});
